import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  HorizontalBars,
  KpiCard,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Clock, ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { ConsentsCard } from '@/features/crm/ConsentsCard';
import { InteractionsPanel } from '@/features/crm/InteractionsPanel';
import { LoyaltyPanel } from '@/features/crm/LoyaltyPanel';
import { activityTone } from '@/features/crm/labels';
import { crmCustomerQueryOptions, crmTimelineQueryOptions } from '@/features/crm/queries';
import { moduleColor, moduleLabel } from '@/features/insights/labels';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort, formatDateTime, formatNumber } from '@/lib/format';

export const Route = createFileRoute('/_app/crm/customers/$customerId')({
  component: CrmCustomerPage,
});

/** Vue 360 d'un client (ADR-025, R-CRM-02). */
function CrmCustomerPage() {
  const { customerId } = Route.useParams();
  const { t, i18n } = useTranslation([
    'crm',
    'customers',
    'reservations',
    'pos',
    'common',
    'errors',
  ]);
  const canRead = useCan('crm.read');
  const query = useQuery(crmCustomerQueryOptions(customerId, canRead));
  const timeline = useInfiniteQuery(crmTimelineQueryOptions(customerId, 20, undefined, canRead));
  const events = useMemo(
    () => timeline.data?.pages.flatMap((page) => page.data) ?? [],
    [timeline.data],
  );
  const data = query.data;
  const currency = data?.currencyCode ?? 'XOF';
  const money = (value: number) => formatAmount(value, i18n.language, currency);
  const number = (value: number) => formatNumber(value, i18n.language);

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('crm:customers.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }
  if (query.isError) {
    return (
      <Container>
        <PageHeader title={t('crm:customers.title')} />
        <ErrorState
          title={t('errors:generic.title')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:trace.copy')}
          traceCopiedLabel={t('common:trace.copied')}
        />
      </Container>
    );
  }
  if (query.isPending || !data) {
    return (
      <Container>
        <Skeleton variant="title" />
        <Skeleton height={120} />
        <Skeleton height={240} />
      </Container>
    );
  }
  const customer = data.customer;
  const emailConsent = customer.grantedChannels.includes('EMAIL');

  return (
    <Container>
      <PageHeader
        title={customer.displayName}
        subtitle={t('crm:detail.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          {
            label: t('crm:customers.title'),
            link: <Link to="/crm/customers">{t('crm:customers.title')}</Link>,
          },
          { label: customer.displayName },
        ]}
        actions={
          <Button variant="secondary" asChild>
            <Link to="/customers/$customerId" params={{ customerId }}>
              <ExternalLink className="ico" aria-hidden="true" />
              {t('crm:detail.openRecord')}
            </Link>
          </Button>
        }
      />

      <div className="stack">
        <div className="row row--wrap" style={{ gap: 6 }}>
          <Badge tone={customer.kind === 'PERSON' ? 'violet' : 'blue'}>
            {t(`customers:kind.${customer.kind}`)}
          </Badge>
          <Badge tone={activityTone(customer.activity)}>
            {t(`crm:activity.${customer.activity}`)}
          </Badge>
          {customer.loyalty?.tierName ? (
            <Badge tone="amber">{customer.loyalty.tierName}</Badge>
          ) : null}
          {customer.tags.map((tag) => (
            <Badge key={tag} tone="green">
              {tag}
            </Badge>
          ))}
        </div>

        <div className="grid grid-4">
          <KpiCard
            label={t('crm:kpi.visits')}
            value={number(customer.visits)}
            footer={t('crm:customers.visitsDetail', {
              stays: customer.staysCount,
              orders: customer.ordersCount,
            })}
            tone="violet"
          />
          <KpiCard
            label={t('crm:kpi.spendTotal')}
            value={money(customer.spendTotal)}
            tone="green"
          />
          <KpiCard
            label={t('crm:kpi.averageTicket')}
            value={data.averageTicket === null ? '—' : money(data.averageTicket)}
            footer={
              data.averageStayValue === null
                ? undefined
                : `${t('crm:kpi.averageStay')} : ${money(data.averageStayValue)}`
            }
            tone="blue"
          />
          <KpiCard
            label={t('crm:kpi.lastVisit')}
            value={
              customer.lastVisitAt ? formatDateShort(customer.lastVisitAt, i18n.language) : '—'
            }
            footer={
              customer.firstVisitAt
                ? `${t('crm:kpi.firstVisit')} : ${formatDateShort(customer.firstVisitAt, i18n.language)}`
                : undefined
            }
            tone="amber"
          />
        </div>

        <div className="grid grid--main-aside">
          <div className="stack">
            <div className="grid grid-2">
              <Card>
                <CardHeader title={t('crm:sections.spendByModule')} />
                <CardBody>
                  {customer.spendByModule.length === 0 ? (
                    <EmptyState title={t('crm:detail.noSpend')} />
                  ) : (
                    <HorizontalBars
                      items={customer.spendByModule.map((row) => ({
                        key: row.module,
                        label: moduleLabel(t, row.module),
                        value: row.amount,
                        display: money(row.amount),
                        color: moduleColor(row.module),
                      }))}
                    />
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader title={t('crm:sections.favorites')} />
                <CardBody>
                  <div className="kv-grid">
                    <div>
                      <div className="kv__k">{t('crm:sections.favoriteOutlet')}</div>
                      <div className="kv__v t-13">
                        {data.favoriteOutlet
                          ? `${data.favoriteOutlet.name} (${number(data.favoriteOutlet.ordersCount)})`
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="kv__k">{t('crm:sections.topProducts')}</div>
                      <div className="kv__v t-13">
                        {data.topProducts.length === 0
                          ? '—'
                          : data.topProducts.map((row) => row.label).join(', ')}
                      </div>
                    </div>
                  </div>
                  {data.spendByYear.length > 0 ? (
                    <div className="mt-3">
                      <div className="kv__k">{t('crm:sections.spendByYear')}</div>
                      <HorizontalBars
                        items={data.spendByYear.map((row) => ({
                          key: String(row.year),
                          label: String(row.year),
                          value: row.amount,
                          display: money(row.amount),
                        }))}
                      />
                    </div>
                  ) : null}
                </CardBody>
              </Card>
            </div>

            <div className="grid grid-2">
              <Card>
                <CardHeader title={t('crm:sections.recentStays')} />
                <CardBody density="flush">
                  {data.recentStays.length === 0 ? (
                    <EmptyState title={t('crm:detail.noStays')} />
                  ) : (
                    <TableWrap>
                      <Table compact>
                        <caption className="sr-only">{t('crm:sections.recentStays')}</caption>
                        <thead>
                          <tr>
                            <Th>{t('crm:columns.reference')}</Th>
                            <Th>{t('crm:columns.arrival')}</Th>
                            <Th align="right">{t('crm:columns.nights')}</Th>
                            <Th align="right">{t('crm:columns.total')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.recentStays.map((stay) => (
                            <Tr key={stay.id}>
                              <Td>
                                <Link
                                  className="link-quiet mono"
                                  to="/reservations/$reservationId"
                                  params={{ reservationId: stay.id }}
                                >
                                  {stay.reference}
                                </Link>
                                <div className="muted t-12">{stay.resourceName ?? ''}</div>
                              </Td>
                              <Td>{formatDateShort(stay.arrivalAt, i18n.language)}</Td>
                              <Td align="right" numeric>
                                {stay.nights}
                              </Td>
                              <Td align="right" numeric>
                                {money(stay.total)}
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader title={t('crm:sections.recentOrders')} />
                <CardBody density="flush">
                  {data.recentOrders.length === 0 ? (
                    <EmptyState title={t('crm:detail.noOrders')} />
                  ) : (
                    <TableWrap>
                      <Table compact>
                        <caption className="sr-only">{t('crm:sections.recentOrders')}</caption>
                        <thead>
                          <tr>
                            <Th>{t('crm:columns.reference')}</Th>
                            <Th>{t('crm:columns.outlet')}</Th>
                            <Th>{t('crm:columns.date')}</Th>
                            <Th align="right">{t('crm:columns.total')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.recentOrders.map((order) => (
                            <Tr key={order.id}>
                              <Td>
                                <span className="mono">{order.reference}</span>
                              </Td>
                              <Td>{order.outletName}</Td>
                              <Td>
                                {formatDateShort(
                                  `${order.serviceDate}T00:00:00.000Z`,
                                  i18n.language,
                                )}
                              </Td>
                              <Td align="right" numeric>
                                {money(order.total)}
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </CardBody>
              </Card>
            </div>

            <LoyaltyPanel customerId={customerId} account={data.loyalty} />
            <InteractionsPanel
              customerId={customerId}
              email={customer.email}
              emailConsent={emailConsent}
            />

            <Card>
              <CardHeader
                title={t('crm:sections.timeline')}
                icon={<Clock className="ico ico-16" aria-hidden="true" />}
                iconTone="blue"
              />
              <CardBody density="flush">
                {timeline.isPending ? (
                  <div className="p-4">
                    <Skeleton height={120} />
                  </div>
                ) : events.length === 0 ? (
                  <EmptyState title={t('crm:timeline.empty')} />
                ) : (
                  <ul className="list-plain">
                    {events.map((event) => (
                      <li key={`${event.kind}-${event.id}`} className="p-3 border-b">
                        <div className="row row--wrap" style={{ gap: 8 }}>
                          <Badge tone="neutral">{t(`crm:timeline.kinds.${event.kind}`)}</Badge>
                          <span className="t-13 strong">{event.title}</span>
                          {event.subtitle ? (
                            <span className="muted t-12">{event.subtitle}</span>
                          ) : null}
                          <span className="muted t-12" style={{ marginLeft: 'auto' }}>
                            {formatDateTime(event.occurredAt, i18n.language)}
                          </span>
                        </div>
                        <div className="muted t-12 mt-1">
                          {event.reference ? (
                            <span className="mono">{event.reference} </span>
                          ) : null}
                          {event.amount !== null ? money(event.amount) : ''}
                          {event.points !== null
                            ? ` ${event.points > 0 ? '+' : ''}${event.points} pts`
                            : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {timeline.hasNextPage ? (
                  <div className="p-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={timeline.isFetchingNextPage}
                      onClick={() => void timeline.fetchNextPage()}
                    >
                      {t('crm:timeline.loadMore')}
                    </Button>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </div>

          <aside className="stack sticky-aside">
            <Card variant="raised">
              <CardHeader title={t('crm:sections.profile')} />
              <CardBody density="tight">
                <div className="kv-grid">
                  <div>
                    <div className="kv__k">{t('customers:fields.phonePrimary')}</div>
                    <div className="kv__v t-13">{customer.phonePrimary ?? '—'}</div>
                  </div>
                  <div>
                    <div className="kv__k">{t('customers:fields.email')}</div>
                    <div className="kv__v t-13">{customer.email ?? '—'}</div>
                  </div>
                  <div>
                    <div className="kv__k">{t('customers:fields.birthDate')}</div>
                    <div className="kv__v t-13">
                      {customer.birthDate
                        ? formatDateShort(`${customer.birthDate}T00:00:00Z`, i18n.language)
                        : '—'}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <ConsentsCard customerId={customerId} consents={data.consents} />
          </aside>
        </div>
      </div>
    </Container>
  );
}
